import {Injectable, NgZone} from "@angular/core";
import * as Log from "~/app/core/log";
import {ApiService} from "~/app/core/api.service";
import {InteractionService} from "~/app/core/interaction.service";
import {InitInfo} from "~/app/core/model/init-info";
import {NavController, Platform} from "@ionic/angular";

export interface NavLocation {
    link: string | Array<any>;
    params?: any;
}

@Injectable()
export class NavigationService {
    private depth: number = 0;

    constructor(
        private readonly navController: NavController,
        private readonly platform: Platform,
    ) {
    }

    navBack() {
        this.navController.back();
        this.logNav("BACK");
        Log.debug(Log.Tag.NavigationService, "NAVIGATE BACK");
        this.depth--;
    }

    navPageShowCashier(offerId: number, doneBack: boolean = false, testing: boolean = false) {
        this.go({
            link: ["/show-cashier", offerId], params: {
                doneBack,
                testing
            }
        }, doneBack ? "forward" : "root");
    }
  navPageOffer(offerId: number) {
    this.forward({link: ["/offer", offerId]});
  }

    navPageThanks(offerId: number, transition: "fade"|"slideLeft" = "fade") {
        this.forward({ link: ["/thanks", offerId] });
    }

    navPageWelcome() {
        this.root({ link: ["/welcome"] });
    }

    navPageLogout() {
        this.root({ link: ["/logout"] });
    }

    navPageUpgrade() {
        this.root({ link: ["/upgrade"] });
    }

    navPagePrivacyPolicy() {
        this.forward({ link: ["/privacy-policy"] });
    }

    navPageTermsOfUse() {
        this.forward({ link: ["/terms-of-use"] });
    }

    navPageHome() {
      this.forward({ link: ["/home"] });
    }

    navPageSearching(api: ApiService = null, offerId: number = null, autoOpen: boolean = false) {
        if (api && api.offers_searchComplete()) {
            Log.debug(Log.Tag.NavigationService, "search complete");
            if (api.offers_haveOffers()) {
                if (api.device_loadSettings().signupBeforeAnyOffers && !api.user_isAuthenticated()) {
                    const info: InitInfo = api.device_info();
                    if (info && info.showLogin) {
                        this.navPageLogin();
                    } else {
                        this.navPageRegister();
                    }
                } else {
                    this.navPageOffers(offerId, autoOpen, "fade", api);
                }
            } else {
                if (api.device_loadSettings().signupBeforeNoOffers && !api.user_isAuthenticated()) {
                    const info: InitInfo = api.device_info();
                    if (info && info.showLogin) {
                        this.navPageLogin();
                    } else {
                        this.navPageRegister();
                    }
                } else {
                    this.navPageNoOffers();
                }
            }
        } else {
            Log.debug(Log.Tag.NavigationService, "search not complete");
            this.root({ link: offerId !== null ? ["/searching", offerId] : ["/searching"], params: { autoOpen: autoOpen } });
        }
    }

    navPageSponsor(allowBack: boolean) {
        this.go({ link: ["/sponsor"], params: { allowBack } }, allowBack ? "forward" : "root");
    }

    navPageSettings() {
        this.root({ link: ["/settings"] });
    }

    navPageNotificationsStatus(allowBack: boolean = false) {
        this.go({ link: ["/notifications-status"], params: { allowBack: allowBack ? "true" : "false" } }, allowBack ? "forward": "root");
    }

    navPageAbout() {
        this.root({ link: ["/about"] });
    }

    navPageContactUs(reason: "support"|"merchants"|"sponsors") {
        this.forward({ link: ["/contact-us", reason] });
    }

    navPageRegister(offerId: number = null, allowBack: boolean = false) {
        this.go({
            link: ["/signup"], params: {
                offerId: offerId,
                show: "register",
                allowBack: allowBack ? "true" : "false"
            }
        }, allowBack ? "forward" : "root");
    }

    navPageLogin(offerId: number = null, allowBack: boolean = false) {
        this.go({
            link: ["/login"], params: {
                offerId: offerId,
                show: "login",
                allowBack: allowBack ? "true" : "false"
            }
        }, allowBack ? "forward" : "root");
    }

  navPageSignup(offerId: number = null, allowBack: boolean = false) {
    this.go({
      link: ["/signup"], params: {
        offerId: offerId,
        show: "signup",
        allowBack: allowBack ? "true" : "false"
      }
    }, allowBack ? "forward" : "root");
  }

    navPageOffers(offerId: number = null, autoOpen: boolean = false, transition: "fade"|"flip"|"slideRight"|"slideLeft" = "fade", api: ApiService = null) {
        if (api && ((api.device_loadSettings().signupBeforeAnyOffers && !api.user_isAuthenticated()) || (!api.offers_searchComplete() || !api.offers_haveOffers()))) {
            if (transition === "slideRight" || transition === "fade") {
                this.navPageSearch(false, transition);
            } else {
                this.navPageSearch(false);
            }
        } else {
            this.root({
                link: ["/offers"], params: {
                    offerId: offerId,
                    autoOpen: autoOpen
                }
            });
        }
    }

    navPageNoOffers() {
        this.root({ link: ["/no-offers"] });
    }

    navPageForgotPassword(offerId: number = null, allowBack: boolean = false) {
        this.go({
            link: ["/forgot-password"], params: {
                offerId: offerId,
                allowBack: allowBack ? "true" : "false"
            }
        }, allowBack ? "forward" : "root");
    }

    navPageValidate(offerId: number = null, allowBack: boolean= false) {
        this.go({
            link: ["/validate"], params: {
                offerId: offerId,
                allowBack: allowBack ? "true" : "false"
            }
        }, allowBack ? "forward" : "root");
    }

    navPageReferrer(offerId: number = null, allowBack: boolean= false) {
        this.go({
            link: ["/referrer"], params: {
                offerId: offerId,
                allowBack: allowBack ? "true" : "false"
            }
        }, allowBack ? "forward" : "root");
    }

    navPageSearch(allowBack: boolean, transition: "fade"|"slideRight" = "fade") {
        this.go({ link: ["/location"], params: { allowBack } }, allowBack ? "forward": "root");
    }

    navPageSponsorVideo(offerId: number) {
        if (offerId) {
            this.root({ link: ["/sponsor-video", offerId]});
        } else {
            this.forward({ link: ["/sponsor-video"]});
        }
    }

    navPageShare(offerId: number) {
        this.forward({ link: ["/share", offerId]});
    }

    navPageShareApp() {
        this.root({ link: ["/share-app"] });
    }

    navMapDirections(address: string) {
        this.logNav("DIRECTIONS: " + address);
        Log.debug(Log.Tag.NavigationService, "Launch maps");
        if (this.platform.is("ios")) {
            window.location.href = "http://maps.apple.com/?daddr=" + encodeURIComponent(address) + "&dirflg=d&t=h";
        } else {
            window.location.href = "http://maps.google.com/maps?daddr=" + encodeURIComponent(address);
        }
    }

    navPageLogs() {
        this.root({ link: ["/logs"] });
    }

    launchSettingsApp() {
        // if (isAndroid) {
        //     const context: android.content.Context = application.android.context;
        //     const intent = new android.content.Intent(android.provider.Settings.ACTION_APPLICATION_DETAILS_SETTINGS);
        //     intent.setData(android.net.Uri.fromParts("package", "com.whutsfree.freestuff", null));
        //     intent.addFlags(android.content.Intent.FLAG_ACTIVITY_NEW_TASK);
        //     context.startActivity(intent);
        // } else {
        //     UIApplication.sharedApplication.openURL(NSURL.URLWithString(UIApplicationOpenSettingsURLString));
        // }
    }

    public forward(location: NavLocation) {
        this.go(location, "forward");
    }

    public root(location: NavLocation) {
        this.go(location, "root");
    }

    public go(location: NavLocation, direction: "forward"|"back"|"root" = "root") {
        let path = null;
        let firstPath = null;
        let navigateCommands;
        if (typeof location.link === 'string') {
            path = location.link;
            navigateCommands = [location.link];
        } else if (Array.isArray(location.link)) {
            location.link.forEach((val) => {
                if (!path) {
                    path = val;
                    firstPath = val;
                } else {
                    path = path + "/" + val;
                }
            });
            navigateCommands = location.link;
        }
        this.logNav(path);
        Log.debug(Log.Tag.NavigationService, "NAVIGATE -> " + path);

        switch (direction) {
            case "root": {
                this.navController.navigateRoot(navigateCommands, { queryParams: location.params, animated: true });
                this.depth = 0;
            } break;
            case "forward": {
                this.navController.navigateForward(navigateCommands, { queryParams: location.params });
                this.depth++;
            } break;
            case "back": {
                this.navController.navigateBack(navigateCommands, { queryParams: location.params });
                this.depth--;
            } break;
        }
    }

    private logNav(whereTo: string) {
        Log.out("***************************************************");
        Log.out(whereTo);
        Log.out("***************************************************");
    }
}
